import React, { forwardRef } from "react";
import { useAgeGroup, useDensity, resolveTouchMin } from "./context";
import type { Density, TouchSpec } from "./context";

/**
 * Button — primitiva condivisa TL4K.
 *
 * Copre i pattern hand-rolled censiti nell'assessment 2026-08-22:
 *   1. bottone primario pieno       → variant="primary"
 *   2. bottone secondario/ghost     → variant="ghost"
 *   3. bottone pericolo/elimina     → variant="danger"
 *   6. bottone icona circolare      → iconOnly + shape="circle"
 *
 * Garanzie che il CSS da solo non poteva dare (perché `.tl4k-admin-btn` era
 * CSS-only e non poteva imporre nulla):
 *  - `type="button"` di default — niente submit accidentali dentro form;
 *  - `aria-label` OBBLIGATORIO a livello di tipi quando `iconOnly`;
 *  - focus ring sempre presente;
 *  - `busy` gestisce aria-busy + disabilitazione, senza rimuovere il label.
 */

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "outline-primary"
  | "ghost"
  | "danger";

export type ButtonSize = "sm" | "md" | "lg";

interface ButtonOwnProps {
  variant?: ButtonVariant;
  /** Densità; se omessa eredita dal DensityProvider, altrimenti "comfortable". */
  density?: Density;
  /**
   * Minimo di area cliccabile. DEFAULT `false` — mai implicito.
   * Obbligatorio (`true` o una fascia) su superfici usate dai bambini.
   */
  touch?: TouchSpec;
  /** Scala tipografica, indipendente da densità e touch. */
  size?: ButtonSize;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
  shape?: "rounded" | "circle";
  /** Mostra uno spinner, imposta aria-busy e blocca il click. */
  busy?: boolean;
  fullWidth?: boolean;
  /**
   * Escape hatch esplicito e grep-abile. Usare solo per il posizionamento
   * (margini, grid-area), mai per ridefinire colori o altezze: quelli
   * appartengono alle varianti.
   */
  className?: string;
}

type ButtonAsButton = ButtonOwnProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps | "type"> & {
    as?: "button";
    type?: "button" | "submit" | "reset";
  };

type ButtonAsAnchor = ButtonOwnProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonOwnProps> & {
    as: "a";
    href: string;
  };

/**
 * Vincolo di compliance imposto dai tipi: un bottone di sole icone DEVE avere
 * un `aria-label`. È una violazione WCAG frequente e invisibile in review —
 * qui non compila.
 */
type IconOnlyConstraint =
  | { iconOnly: true; "aria-label": string }
  | { iconOnly?: false | undefined };

export type ButtonProps = (ButtonAsButton | ButtonAsAnchor) & IconOnlyConstraint;

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    const {
      variant = "primary",
      density: densityProp,
      touch = false,
      size = "md",
      iconStart,
      iconEnd,
      iconOnly,
      shape = "rounded",
      busy = false,
      fullWidth = false,
      className,
      children,
      style,
      ...rest
    } = props as ButtonOwnProps & {
      iconOnly?: boolean;
      children?: React.ReactNode;
      style?: React.CSSProperties;
      as?: "button" | "a";
      [key: string]: unknown;
    };

    const ageGroup = useAgeGroup();
    const density = useDensity(densityProp);
    const touchMin = resolveTouchMin(touch, ageGroup);

    if (import.meta.env.DEV) {
      // Nessun cestino muto per i bambini: un'azione distruttiva deve avere
      // un'etichetta testuale o almeno un aria-label esplicito.
      if (variant === "danger" && !children && !rest["aria-label"]) {
        console.warn(
          "[TL4K] <Button variant=\"danger\"> senza testo né aria-label. " +
            "Un'azione distruttiva deve essere nominata, non solo disegnata.",
        );
      }
    }

    const classes = cx(
      "tl4k-btn",
      `tl4k-btn--${variant}`,
      `tl4k-btn--${density}`,
      size !== "md" && `tl4k-btn--${size}`,
      shape === "circle" && "tl4k-btn--circle",
      iconOnly && "tl4k-btn--icon-only",
      fullWidth && "tl4k-btn--full-width",
      className,
    );

    const mergedStyle: React.CSSProperties = {
      ...(touchMin ? ({ "--tl4k-btn-touch-min": touchMin } as React.CSSProperties) : null),
      // I 4-6 anni hanno bisogno di transizioni più lente (token `slow`).
      ...(ageGroup === "4-6"
        ? ({ "--tl4k-btn-transition": "var(--tl4k-transition-slow, 350ms ease)" } as React.CSSProperties)
        : null),
      ...style,
    };

    const content = (
      <>
        {busy ? <span className="tl4k-btn__spinner" aria-hidden="true" /> : iconStart}
        {children}
        {iconEnd}
      </>
    );

    if ((props as ButtonAsAnchor).as === "a") {
      const { as: _as, ...anchorRest } = rest as Record<string, unknown>;
      return (
        <a
          {...(anchorRest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          style={mergedStyle}
          aria-busy={busy || undefined}
        >
          {content}
        </a>
      );
    }

    const {
      as: _as,
      type = "button",
      disabled,
      ...buttonRest
    } = rest as Record<string, unknown> & {
      type?: "button" | "submit" | "reset";
      disabled?: boolean;
    };

    return (
      <button
        {...(buttonRest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        ref={ref as React.Ref<HTMLButtonElement>}
        // Default esplicito: dentro un <form>, "submit" è il default HTML e
        // produce invii accidentali. Qui si sceglie, non si eredita.
        type={type}
        className={classes}
        style={mergedStyle}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
      >
        {content}
      </button>
    );
  },
);
